import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Toast } from 'antd-mobile';
import { useI18n } from '../../../hooks/useI18n';
import { getSupervisorScenarioDetail, SupervisorScenarioInfo, getUploadCount, UploadCountResponse } from '@/lib/api/ai-supervisor';
import ManyDotLoading from '@/components/Loaders/ManyDotLoading';
import PCMaxWidthContainer from '@/components/Layout/PCMaxWidthContainer';
import jiangbei from '@/assets/images/dudao/jiangbei.png'
import { formatTextWithLineBreaks } from '../../../utils/textUtils';

const SupervisorDetail: React.FC = () => {
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();

  const [scenario, setScenario] = useState<SupervisorScenarioInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploadCount, setUploadCount] = useState<UploadCountResponse | null>(null);

  // 获取场景详情
  const fetchScenarioDetail = async () => {
    if (!scenarioId) return;

    setLoading(true);
    try {
      const response = await getSupervisorScenarioDetail(scenarioId);
      if (response.success && response.data) {
        setScenario(response.data);
      } else {
        Toast.show({ content: '获取场景详情失败', icon: 'fail' });
      }
    } catch (error) {
      console.error('获取场景详情失败:', error);
      Toast.show({ content: '获取场景详情失败', icon: 'fail' });
    } finally {
      setLoading(false);
    }
  };

  // 获取上传次数
  const fetchUploadCount = async () => {
    if (!scenarioId) return;

    try {
      const response = await getUploadCount(scenarioId);
      if (response.success && response.data) {
        setUploadCount(response.data);
      }
    } catch (error) {
      console.error('获取上传次数失败:', error);
    }
  };

  useEffect(() => {
    fetchScenarioDetail();
    fetchUploadCount();
  }, [scenarioId]);

  // 查看分析记录
  const handleViewRecords = () => {
    navigate(`/ai-supervisor/records/${scenarioId}`);
  };

  // 查看排行榜
  const handleViewRank = () => {
    navigate(`/ai-supervisor/rank-list/${scenarioId}`);
  };

  // 开始分析
  const handleStartAnalysis = () => {
    // 检查是否可以上传
    if (uploadCount && !uploadCount.can_upload) {
      Toast.show({
        content: `已达到最大上传次数限制（${uploadCount.max_upload_limit}次）`,
        icon: 'fail'
      });
      return;
    }
    navigate(`/ai-supervisor/upload/${scenarioId}`);
  };

  if (loading) {
    return (
      <PCMaxWidthContainer bgGradient='bg-gradient-to-b from-[#f8f9ff] to-[#ffffff]'>
        <div className='min-h-screen flex items-center justify-center'>
          <ManyDotLoading />
        </div>
      </PCMaxWidthContainer>
    );
  }

  if (!scenario) {
    return (
      <PCMaxWidthContainer bgGradient='bg-gradient-to-b from-[#f8f9ff] to-[#ffffff]'>
        <div className='min-h-screen flex items-center justify-center'>
          <div className='text-[#909399] text-sm'>场景不存在</div>
        </div>
      </PCMaxWidthContainer>
    );
  }

  return (
    <PCMaxWidthContainer bgGradient='bg-[#f5f7fa]'>
      <div className='pt-4'>

      <div className='px-[15px] mb-[100px]'>
        {/* 场景名称卡片 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className='bg-white rounded-[8px] mb-[20px]'
        >
          <div className='px-4 pt-4 pb-[26px]'>
            <div className='mb-[24px]'>
              <div className='flex items-center'>
                <span className='text-[#485066] text-[16px] font-medium leading-[24px]'>
                  场景名称
                </span>
              </div>
              <div
                className='h-[8px] w-[64px] mb-4'
                style={{
                  backgroundImage: 'linear-gradient(-86.921202882757deg, rgba(230, 244, 255, 1) 0.33138%, rgba(249, 240, 255, 1) 99.664%)'
                }}
              ></div>
            </div>
            <div className='text-[#86909c] text-[14px] leading-[1.6]'>
              {scenario.name || '场景名称'}
            </div>
          </div>
        </motion.div>

        {/* 场景描述卡片 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className='bg-white rounded-[8px] mb-[20px]'
        >
          {/* 场景描述部分 */}
          <div className='px-4 pt-4 pb-[26px]'>
            <div className='mb-[24px]'>
              <div className='flex items-center'>
                <span className='text-[#485066] text-[16px] font-medium leading-[24px]'>
                  场景描述
                </span>
              </div>
              <div
                className='h-[8px] w-[64px] mb-4'
                style={{
                  backgroundImage: 'linear-gradient(-86.921202882757deg, rgba(230, 244, 255, 1) 0.33138%, rgba(249, 240, 255, 1) 99.664%)'
                }}
              ></div>
            </div>
            <div className='text-[#86909c] text-[14px] leading-[1.6]'>
              <div dangerouslySetInnerHTML={{ __html: formatTextWithLineBreaks(scenario.description || '场你作为授客学堂的销售人员与潜在客户的初次接触。需要你学握如何在首次接触中给客户留下深刻的第一印象，成功引1起客户的兴趣，并有效挖掘客户需求，促进成交。') }} />
            </div>
          </div>
        </motion.div>

        {/* 考核标准卡片 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className='bg-white rounded-[8px]'
        >
          <div className='px-4 pt-4 pb-[26px]'>
            <div className='mb-[24px]'>
              <div className='flex items-center'>
                <span className='text-[#485066] text-[16px] font-medium leading-[24px]'>
                  考核标准
                </span>
              </div>
              <div
                className='h-[8px] w-[64px] mb-4'
                style={{
                  backgroundImage: 'linear-gradient(-86.921202882757deg, rgba(230, 244, 255, 1) 0.33138%, rgba(249, 240, 255, 1) 99.664%)'
                }}
              ></div>
            </div>

            {/* 考核关键点 */}
            <div className='mb-[30px]'>
              <div className='flex items-center gap-2 mb-[10px]'>
                <div className='w-1 h-1 rounded-full bg-[#485066]'></div>
                <span className='text-[#485066] text-[14px] leading-[1.6]'>
                  考核关键点
                </span>
              </div>
              <div className='text-[#86909c] text-[14px] leading-[1.6]'>
                <div dangerouslySetInnerHTML={{ __html: formatTextWithLineBreaks(scenario.key_points || '场你作为授客学堂的销售人员与潜在客户的初次接触。需要你学握如何在首次接触中给客户留下深刻的第一印象，成功引1起客户的兴趣，并有效挖掘客户需求，促进成交。') }} />
              </div>
            </div>

            {/* 督导规则 */}
            <div>
              <div className='flex items-center gap-2 mb-[10px]'>
                <div className='w-1 h-1 rounded-full bg-[#485066]'></div>
                <span className='text-[#485066] text-[14px] leading-[1.6]'>
                  督导规则
                </span>
              </div>
              <div className='flex flex-wrap items-center gap-2'>
                <div className='border border-[#faad14] border-solid rounded-[30px] px-2 py-[2px]'>
                  <span className='text-[#faad14] text-[12px] leading-[1.6]'>
                    成绩要求≥{scenario.passing_score}分
                  </span>
                </div>
                <div className='border border-[#faad14] border-solid rounded-[30px] px-2 py-[2px]'>
                  <span className='text-[#faad14] text-[12px] leading-[1.6]'>
                    最大上传次数：{scenario.max_upload_limit ? scenario.max_upload_limit + '次' : '不限'}
                  </span>
                </div>
                {/*{uploadCount && uploadCount.max_upload_limit !== null && (*/}
                {/*  <div className='border border-[#52c41a] border-solid rounded-[30px] px-2 py-[2px]'>*/}
                {/*    <span className='text-[#52c41a] text-[12px] leading-[1.6]'>*/}
                {/*      已上传：{uploadCount.upload_count}次 / 剩余：{uploadCount.remaining_count}次*/}
                {/*    </span>*/}
                {/*  </div>*/}
                {/*)}*/}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* 底部操作按钮 */}
      <div className='fixed left-0 right-0 bottom-0 bg-white h-[68px] px-[15px] flex items-center gap-[10px]'>
        {/* 历史记录按钮 */}
        <button
          onClick={handleViewRank}
          className='w-12 h-12 rounded-full bg-white backdrop-blur-[2px] flex items-center justify-center flex-shrink-0'
          style={{
            boxShadow: '0px 0px 13.6px -1px rgba(53,14,145,0.08)'
          }}
        >
          <img
            src={jiangbei}
            alt=""
            className='w-[30px]'
          />
        </button>

        {/* 分析记录按钮 */}
        <button
          onClick={handleViewRecords}
          className='flex-1 h-[48px] rounded-[36px] border border-[#64adff] border-solid bg-transparent flex items-center justify-center min-w-0'
        >
          <span
            className='text-[16px] leading-[1.6] font-normal'
            style={{
              background: 'linear-gradient(90deg, #64adff 0%, #5a6fff 50%, #998dff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            分析记录
          </span>
        </button>

        {/* 上传音频按钮 */}
        <button
          onClick={handleStartAnalysis}
          disabled={uploadCount ? !uploadCount.can_upload : false}
          className='flex-1 h-[48px] rounded-[32px] flex items-center justify-center relative overflow-hidden min-w-0 disabled:opacity-50 disabled:cursor-not-allowed'
          style={{
            background: uploadCount && !uploadCount.can_upload
              ? '#d9d9d9'
              : 'linear-gradient(90deg, #64adff 0%, #5a6fff 50%, #998dff 100%)'
          }}
        >
          <span className='text-white text-[16px] leading-[1.6] font-normal relative z-10'>
            {uploadCount && !uploadCount.can_upload ? '已达上传上限' : '上传音视频'}
          </span>
        </button>
      </div>
      </div>
    </PCMaxWidthContainer>
  );
};

export default SupervisorDetail;

